import { useTranslation } from "react-i18next";
import React from "react";
import { Button } from "semantic-ui-react";
import moment from "moment";
import {
  findApplicableFascia,
  parseTimeToHours,
  isValidTimePoint,
} from "../../utils/fasceSelezioneOreUtils";

const Timeline = ({
  timeSlots = [],
  tempTimeRange,
  onTimeSlotClick,
  getSelectionInstructionText,
  showConfirmButton = false,
  onTimeRangeConfirm,
  onClearTimeRange,
  minHour,
  maxHour,
  minOreAcquistabili,
  maxOreAcquistabiliAlGiorno,
  permettiEccezioneMinOreInizio,
  permettiEccezioneMinOreFine,
  data,
  fasceSelezioneOre = [],
  currentDate = null,
}) => {
  const { t } = useTranslation();

  //helpers
  const handleFirstClick = (slot) => {
    if (!tempTimeRange || !tempTimeRange.start) {
      const slotStartTime = getHourFromTimeString(slot.start);

      // Get applicable fascia
      const fascia = findApplicableFascia(
        fasceSelezioneOre,
        currentDate,
        slot.start,
      );

      // Calculate end time using minOreAcquistabili
      let endTime = slotStartTime + minOreAcquistabili;

      // Adjust to match fascia rules if they exist
      if (fascia && fascia.soloOreIntere) {
        // If soloOreIntere, ensure minOreAcquistabili is rounded up to whole hour
        endTime = slotStartTime + Math.ceil(minOreAcquistabili);
      }

      // Convert endTime back to HH:mm format
      const endHour = Math.floor(endTime);
      const endMinute = (endTime % 1) * 60;
      const endTimeString = `${endHour.toString().padStart(2, "0")}:${endMinute
        .toString()
        .padStart(2, "0")}`;

      // console.log("First click - setting range to minOreAcquistabili:", {
      //   start: slot.start,
      //   end: endTimeString,
      //   minOreAcquistabili,
      //   fascia,
      // });

      // Create a modified slot with the correct end time
      const modifiedSlot = {
        ...slot,
        end: endTimeString,
        _isFullRange: true, // Special marker to indicate this is a full range, not just a slot
        isCustomRange: true,
      };
      onTimeSlotClick(modifiedSlot);
      return;
    }
  };

  const handleSubsequentClick = (slot) => {
    // If we already have a range
    if (tempTimeRange && tempTimeRange.start && tempTimeRange.end) {
      const slotTime = getHourFromTimeString(slot.start); // Start time of the clicked slot

      // Current range details
      const currentStartTime = getHourFromTimeString(tempTimeRange.start);
      const currentEndTime = getHourFromTimeString(tempTimeRange.end);

      // If clicking within the current range (including the starting slot), restart the selection with minOreAcquistabili
      if (slotTime >= currentStartTime && slotTime < currentEndTime) {
        const slotStartTime = getHourFromTimeString(slot.start);

        // Get applicable fascia and calculate increment
        const fascia = findApplicableFascia(
          fasceSelezioneOre,
          currentDate,
          slot.start,
        );
        const increment = fascia?.soloOreIntere ? 1.0 : 0.5;

        // Calculate end time using the appropriate increment
        let endTime = slotStartTime + Math.max(minOreAcquistabili, increment);

        // Adjust to match fascia rules if they exist
        if (fascia) {
          endTime = Math.ceil(endTime / increment) * increment;
        }

        // Convert endTime back to HH:mm format
        const endHour = Math.floor(endTime);
        const endMinute = (endTime % 1) * 60;
        const endTimeString = `${endHour
          .toString()
          .padStart(2, "0")}:${endMinute.toString().padStart(2, "0")}`;

        console.log(
          "Click within range - restarting with minOreAcquistabili:",
          {
            start: slot.start,
            end: endTimeString,
            minOreAcquistabili,
            currentRange: `${tempTimeRange.start} - ${tempTimeRange.end}`,
            fascia,
          },
        );

        // Create a slot that represents the new desired range
        // We need to create a slot with both the correct start AND end
        const newRangeSlot = {
          start: slot.start,
          end: endTimeString,
          _isFullRange: true, // Special marker to indicate this is a full range, not just a slot
          isCustomRange: true,
        };

        onTimeSlotClick(newRangeSlot);
        return;
      }

      // Clicking outside the current range - calculate new extended range
      let newStart, newEnd;

      if (slotTime < currentStartTime) {
        // Extending start backward
        newStart = slot.start;
        newEnd = tempTimeRange.end;
      } else {
        // Extending end forward
        newStart = tempTimeRange.start;
        newEnd = slot.end;
      }

      console.log("Extending range:", {
        from: `${tempTimeRange.start} - ${tempTimeRange.end}`,
        to: `${newStart} - ${newEnd}`,
      });

      const extendedRangeSlot = {
        start: newStart,
        end: newEnd,
        _isFullRange: true,
        isCustomRange: true,
      };

      onTimeSlotClick(extendedRangeSlot);
      return;
    }
  };

  const handleTimeSlotClick = (slot) => {
    console.log("Clicked slot:", slot);

    // Check if this slot can be selected based on business rules
    const canSelect = canSelectSlot(slot);
    if (!canSelect) {
      console.log("Slot cannot be selected due to business rules");
      return;
    }

    // If no selection yet, or if clicking within the current range, handle as a first click/restart
    if (
      !tempTimeRange ||
      !tempTimeRange.start ||
      (getHourFromTimeString(slot.start) >=
        getHourFromTimeString(tempTimeRange.start) &&
        getHourFromTimeString(slot.start) <
          getHourFromTimeString(tempTimeRange.end))
    ) {
      handleFirstClick(slot);
    } else {
      // Otherwise, it's an extension of an existing range
      handleSubsequentClick(slot);
    }
    // Note: onTimeSlotClick is called by handleFirstClick and handleSubsequentClick
  };

  // Helper functions to extract hour and minute values from time strings (HH:mm:ss format)
  const parseTimeString = (timeString) => {
    if (typeof timeString === "string") {
      const [hours, minutes] = timeString
        .split(":")
        .map((num) => parseInt(num, 10));
      return { hours, minutes: minutes || 0 };
    }
    return { hours: timeString, minutes: 0 }; // fallback for backward compatibility
  };

  const getHourFromTimeString = (timeString) => {
    const { hours, minutes } = parseTimeString(timeString);
    return hours + minutes / 60;
  };

  // Helper function to adjust time to match fascia rules
  const minHourNum = getHourFromTimeString(minHour);
  const maxHourNum = getHourFromTimeString(maxHour);

  // Generate time slots for the entire day (24 hours)
  const generateFullDayTimeSlots = () => {
    const slots = [];
    const startHour = Math.floor(minHourNum);
    const endHour = Math.ceil(maxHourNum);

    for (let hour = startHour; hour < endHour; hour++) {
      const timeStr = hour.toString().padStart(2, "0") + ":00";
      const nextHour = (hour + 1).toString().padStart(2, "0") + ":00";
      const halfHour = hour.toString().padStart(2, "0") + ":30";

      // Only add slots that fall within the minHour/maxHour range (using parseTimeToHours for consistency)
      const firstHalfStart = parseTimeToHours(timeStr);
      const secondHalfStart = hour + 0.5;

      // Show all slots - tipoOrarioIntero will control selectability, not visibility
      const shouldIncludeFirstHalf =
        firstHalfStart >= minHourNum && firstHalfStart < maxHourNum;

      const shouldIncludeSecondHalf =
        secondHalfStart >= minHourNum && secondHalfStart < maxHourNum;

      // First half of the hour
      if (shouldIncludeFirstHalf) {
        slots.push({
          start: timeStr,
          end: halfHour,
          display: timeStr.slice(0, 2),
          fullTime: timeStr,
          isFirstHalf: true,
        });
      }

      // Second half of the hour
      if (shouldIncludeSecondHalf) {
        slots.push({
          start: halfHour,
          end: nextHour,
          display: "30",
          fullTime: halfHour,
          isFirstHalf: false,
        });
      }
    }
    return slots;
  };

  // Get availability status for a time slot
  const getSlotAvailability = (currentSlot) => {
    const slotTime = getHourFromTimeString(currentSlot.start);

    // Before minHour or after maxHour - disabled
    if (slotTime < minHourNum || slotTime >= maxHourNum) {
      return "disabled";
    }

    // Check if it's in available time ranges
    // Convert times to numeric values for proper comparison
    // Handle both {start, end} and {dalle, alle} formats
    const isAvailable = timeSlots.some((timeSlot) => {
      const rangeStart = getHourFromTimeString(
        timeSlot.start || timeSlot.dalle,
      );
      const rangeEnd = getHourFromTimeString(timeSlot.end || timeSlot.alle);
      return slotTime >= rangeStart && slotTime < rangeEnd;
    });

    return isAvailable ? "available" : "blocked";
  };

  // Helper to check if a slot can be clicked based on business rules
  const canSelectSlot = (slot) => {
    const availability = getSlotAvailability(slot);
    if (availability !== "available") return false;

    const slotTime = getHourFromTimeString(slot.start);
    const slotEndTime = getHourFromTimeString(slot.end);

    // Get fascia rules
    const fascia = findApplicableFascia(
      fasceSelezioneOre,
      currentDate,
      slot.start,
    );

    // Check fascia rules - only validate start point if no selection exists yet
    if (!tempTimeRange || !tempTimeRange.start) {
      if (fascia && !isValidTimePoint(slot.start, fascia.tipoOrarioIntero)) {
        return false; // Starting slot doesn't match the required time point type
      }
    } else {
      // If we have a selection, check if clicking this slot would create a valid duration
      const startTime = getHourFromTimeString(tempTimeRange.start);

      // Calculate what the duration would be if we extend to this slot
      let potentialEndTime;

      if (slotTime < startTime) {
        // Extending/moving start backward - duration from new start to current end
        potentialEndTime = getHourFromTimeString(tempTimeRange.end);
        const duration = potentialEndTime - slotTime;

        // Check if this duration is valid according to soloOreIntere
        if (fascia && fascia.soloOreIntere) {
          const isWholeHour = Math.abs(duration - Math.round(duration)) < 0.01;
          if (!isWholeHour) return false;
        }
      } else if (slotTime >= getHourFromTimeString(tempTimeRange.end)) {
        // Extending end forward - duration from current start to end of clicked slot
        const duration = slotEndTime - startTime;

        // Check if this duration is valid according to soloOreIntere
        if (fascia && fascia.soloOreIntere) {
          const isWholeHour = Math.abs(duration - Math.round(duration)) < 0.01;
          if (!isWholeHour) return false;
        }
      }
      // If clicking within range, it's a restart, which will be handled by handleFirstClick
    }

    // If no selection yet, check if we can start here
    if (!tempTimeRange || !tempTimeRange.start) {
      // Find the available range containing this slot
      const containingRange = timeSlots.find((range) => {
        const rangeStart = getHourFromTimeString(range.start || range.dalle);
        const rangeEnd = getHourFromTimeString(range.end || range.alle);
        return slotTime >= rangeStart && slotTime < rangeEnd;
      });

      if (!containingRange) return false;

      // Get fascia rules for this time
      const fascia = findApplicableFascia(
        fasceSelezioneOre,
        currentDate,
        slot.start,
      );

      // Adjust minOreAcquistabili based on fascia rules
      const adjustedMinOre = fascia?.soloOreIntere
        ? Math.ceil(minOreAcquistabili)
        : minOreAcquistabili;

      // Check if starting here and extending by minOreAcquistabili would fit in the available range
      const rangeEnd = getHourFromTimeString(
        containingRange.end || containingRange.alle,
      );
      const wouldEndAt = slotTime + adjustedMinOre;

      // Must have enough space to fit minOreAcquistabili
      if (wouldEndAt > rangeEnd) {
        return false;
      }

      // Check if starting here would leave a gap smaller than minOreAcquistabili at the beginning
      if ((!fascia?.permettiBucoMezzora ?? false) && minOreAcquistabili) {
        const rangeStart = getHourFromTimeString(
          containingRange.start || containingRange.dalle,
        );
        const gapAtStart = slotTime - rangeStart;

        if (gapAtStart > 0 && gapAtStart < minOreAcquistabili) {
          // console.log("BLOCKED: Gap at start is too small", {
          //   rangeStart,
          //   slotTime,
          //   gapAtStart,
          //   minOreAcquistabili,
          // });
          return false;
        }
      }

      // Check if starting here would leave a gap smaller than minOreAcquistabili at the end
      // if (!permettiEccezioneMinOreFine && minOreAcquistabili) {
      //   const gapAtEnd = rangeEnd - wouldEndAt;

      //   if (gapAtEnd > 0 && gapAtEnd < minOreAcquistabili) {
      //     // console.log("BLOCKED: Gap at end is too small", {
      //     //   rangeEnd,
      //     //   wouldEndAt,
      //     //   gapAtEnd,
      //     //   minOreAcquistabili,
      //     // });
      //     return false;
      //   }
      // }

      return true;
    }

    // If we already have a range, check if we can extend/shrink to this slot
    if (tempTimeRange.start && tempTimeRange.end) {
      const currentStartTime = getHourFromTimeString(tempTimeRange.start);
      const currentEndTime = getHourFromTimeString(tempTimeRange.end);

      // Determine what kind of operation this would be
      let newStartTime, newEndTime;

      if (slotTime < currentStartTime) {
        // Extending/moving start backward
        newStartTime = slotTime;
        newEndTime = currentEndTime; // Keep current end
      } else if (slotTime >= currentEndTime) {
        // Extending end forward
        newStartTime = currentStartTime;
        newEndTime = slotTime + 0.5; // End of clicked slot
      } else {
        // Clicking within current range - treat as a restart request
        const containingRange = timeSlots.find((range) => {
          const rangeStart = getHourFromTimeString(range.start);
          const rangeEnd = getHourFromTimeString(range.end);
          return slotTime >= rangeStart && slotTime < rangeEnd;
        });

        if (!containingRange) {
          return false;
        }

        // Get fascia rules for this time
        const fascia = findApplicableFascia(
          fasceSelezioneOre,
          currentDate,
          slot.start,
        );

        // Adjust minOreAcquistabili based on fascia rules
        const adjustedMinOre = fascia?.soloOreIntere // If soloOreIntere is true, round up minOreAcquistabili to the nearest whole number
          ? Math.ceil(minOreAcquistabili)
          : minOreAcquistabili;

        const rangeStart = getHourFromTimeString(containingRange.start);
        const rangeEnd = getHourFromTimeString(containingRange.end);
        const proposedStart = slotTime;
        const proposedEnd = proposedStart + adjustedMinOre;

        if (proposedEnd > rangeEnd) {
          return false;
        }

        if (!permettiEccezioneMinOreInizio && minOreAcquistabili) {
          const gapAtStart = proposedStart - rangeStart;

          if (gapAtStart > 0 && gapAtStart < minOreAcquistabili) {
            return false;
          }
        }

        if (!permettiEccezioneMinOreFine && minOreAcquistabili) {
          const gapAtEnd = rangeEnd - proposedEnd;

          if (gapAtEnd > 0 && gapAtEnd < minOreAcquistabili) {
            return false;
          }
        }

        const proposedDuration = proposedEnd - proposedStart;
        if (proposedDuration > maxOreAcquistabiliAlGiorno) {
          // console.log("BLOCKED: Would exceed maximum hours", {
          //   proposedDuration,
          //   maxOreAcquistabiliAlGiorno,
          // });
          return false;
        }

        // Allow the click - handleTimeSlotClick will create the new range selection
        return true;
      }

      // Check if new range would meet minimum hours
      const newDuration = newEndTime - newStartTime;
      if (newDuration < minOreAcquistabili) {
        // console.log("BLOCKED: Would be below minimum hours", {
        //   newDuration,
        //   minOreAcquistabili,
        // });
        return false;
      }

      // NOTE: Don't check soloOreIntere here - allow intermediate durations while building selection
      // The final validation will happen when the user confirms the selection

      // Check if new range would exceed maximum hours
      if (newDuration > maxOreAcquistabiliAlGiorno) {
        // console.log("BLOCKED: Would exceed maximum hours", {
        //   newDuration,
        //   maxOreAcquistabiliAlGiorno,
        // });
        return false;
      }

      // CRITICAL: Check that all time slots between newStartTime and newEndTime are available
      // This prevents selecting across gaps in availability
      const isRangeContinuouslyAvailable = (start, end) => {
        // Check every half-hour slot in the range (assuming 30-minute granularity)
        for (let time = start; time < end; time += 0.5) {
          const isAvailable = timeSlots.some((range) => {
            const rangeStart = getHourFromTimeString(range.start);
            const rangeEnd = getHourFromTimeString(range.end);
            return time >= rangeStart && time < rangeEnd;
          });
          if (!isAvailable) {
            return false;
          }
        }
        return true;
      };

      if (!isRangeContinuouslyAvailable(newStartTime, newEndTime)) {
        // console.log("BLOCKED: Range contains unavailable slots", {
        //   newStartTime,
        //   newEndTime,
        // });
        return false;
      }

      // Find the available range containing the selection
      const startContainingRange = timeSlots.find((range) => {
        const rangeStart = getHourFromTimeString(range.start);
        const rangeEnd = getHourFromTimeString(range.end);
        return currentStartTime >= rangeStart && currentStartTime < rangeEnd;
      });

      if (!startContainingRange) return false;

      // Check if the new end would still be in range
      if (newEndTime > getHourFromTimeString(startContainingRange.end)) {
        // console.log("BLOCKED: Would extend beyond available range");
        return false;
      }

      // Check if extending end would leave a gap smaller than minOreAcquistabili
      if (
        slotTime >= currentEndTime &&
        !permettiEccezioneMinOreFine &&
        minOreAcquistabili
      ) {
        const rangeEnd = getHourFromTimeString(startContainingRange.end);
        const gapAtEnd = rangeEnd - newEndTime;

        if (gapAtEnd > 0 && gapAtEnd < minOreAcquistabili) {
          // console.log("BLOCKED: Would leave gap at end too small", {
          //   rangeEnd,
          //   newEndTime,
          //   gapAtEnd,
          //   minOreAcquistabili,
          // });
          return false;
        }
      }

      // Check if moving start would leave a gap smaller than minOreAcquistabili
      if (
        slotTime < currentStartTime &&
        !permettiEccezioneMinOreInizio &&
        minOreAcquistabili
      ) {
        const rangeStart = getHourFromTimeString(startContainingRange.start);
        const gapAtStart = newStartTime - rangeStart;

        if (gapAtStart > 0 && gapAtStart < minOreAcquistabili) {
          return false;
        }
      }

      return true;
    }

    return false;
  };

  // Check if time slot is selected
  const isTimeSlotSelected = (slot) => {
    if (!tempTimeRange || !tempTimeRange.start) return false;

    // If we only have a start time, check if this slot matches the start
    if (!tempTimeRange.end) {
      return slot.start === tempTimeRange.start;
    }

    // If we have both start and end, check if this slot is within the range
    return slot.start >= tempTimeRange.start && slot.start < tempTimeRange.end;
  };

  // Get slot style based on availability and selection
  const getSlotStyle = (slot) => {
    const availability = getSlotAvailability(slot);
    const isSelected = isTimeSlotSelected(slot);
    const canSelect = canSelectSlot(slot);

    const baseStyle = {
      display: "inline-block",
      width: "30px",
      height: "40px",
      lineHeight: "40px",
      textAlign: "center",
      fontSize: "11px",
      cursor: availability === "available" && canSelect ? "pointer" : "default",
      border: "1px solid #ddd",
      borderRight: slot.isFirstHalf ? "0" : "1px solid #ddd",
      verticalAlign: "top",
      userSelect: "none",
    };

    if (availability === "disabled") {
      return {
        ...baseStyle,
        backgroundColor: "#333",
        color: "#666",
        cursor: "not-allowed",
      };
    } else if (availability === "blocked") {
      return {
        ...baseStyle,
        backgroundColor: "#f5f5f5",
        color: "#999",
        backgroundImage:
          "repeating-linear-gradient(45deg, transparent, transparent 2px, #ddd 2px, #ddd 4px)",
      };
    } else if (isSelected) {
      return {
        ...baseStyle,
        backgroundColor: "#2185d0",
        color: "white",
      };
    } else if (!canSelect) {
      // Available but not selectable due to business rules
      return {
        ...baseStyle,
        backgroundColor: "#fff3cd",
        color: "#856404",
        cursor: "not-allowed",
        opacity: 0.6,
      };
    } else {
      return {
        ...baseStyle,
        backgroundColor: "#e8f5e8",
        color: "#2c662d",
      };
    }
  };

  return (
    <>
      {/* <div style={{ marginBottom: "10px" }}>
        <Message info size="small">
          {getSelectionInstructionText()}
        </Message>
      </div> */}

      {/* Timeline visualization */}
      <div
        style={{
          display: "flex", // Added for centering
          justifyContent: "center", // Added for centering
          flexDirection: "column", // Stack elements vertically
          alignItems: "center", // Center align all children
        }}
        className="timeline_box"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
          <div
            style={{
              border: "2px solid #ddd",
              borderRadius: "8px",
              padding: "15px",
              backgroundColor: "#fafafa",
              fontFamily: "monospace",
              position: "relative", // Added for positioning the label
            }}
          >
            {/* Data label in upper left corner */}
            {data && (
              <div
                style={{
                  position: "absolute",
                  top: "2px",
                  left: "8px",
                  //backgroundColor: "#2185d0",
                  //color: "white",
                  padding: "2px 6px",
                  borderRadius: "3px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  zIndex: 10,
                }}
              >
                {data}
              </div>
            )}
            {/* Container for cancel button and timeline */}
            <div
              style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}
            >
              {/* Cancel button on the left */}
              {tempTimeRange && tempTimeRange.start && (
                <div style={{ paddingTop: "20px" }}>
                  <Button
                    icon="close"
                    size="mini"
                    color="red"
                    circular
                    onClick={onClearTimeRange}
                    title={t("cancella_range") || "Cancella range"}
                    style={{
                      width: "24px",
                      height: "24px",
                      padding: "0",
                      minWidth: "24px",
                      marginTop: data ? "24px" : "10px",
                    }}
                  />
                </div>
              )}

              {/* Timeline container */}
              <div style={{ flex: 1 }}>
                {/* Header with time labels */}
                <div
                  style={{
                    display: "flex",
                    marginTop: data ? "10px" : "-5px",
                    marginBottom: "5px",
                    alignItems: "flex-end",
                  }}
                >
                  {Array.from(
                    { length: Math.ceil(maxHourNum) - Math.floor(minHourNum) },
                    (_, i) => {
                      const hour = Math.floor(minHourNum) + i;
                      return (
                        <React.Fragment key={hour}>
                          <div
                            style={{
                              width: "60px",
                              textAlign: "left",
                              fontSize: "11px",
                              color: "#333",
                              fontWeight: "bold",
                              borderRight: i < 9 ? "1px solid #ddd" : "none",
                              paddingLeft: "2px",
                              display: "inline-block",
                              position: "relative",
                            }}
                          >
                            {hour.toString().padStart(2, "0")}
                            {/* "30" label between hours, except after last hour */}
                            {i <
                              Math.ceil(maxHourNum) -
                                Math.floor(minHourNum) -
                                1 && (
                              <span
                                style={{
                                  position: "absolute",
                                  left: "30px",
                                  bottom: "-10px",
                                  fontSize: "9px",
                                  color: "#666",
                                  fontWeight: "normal",
                                }}
                              >
                                30
                              </span>
                            )}
                          </div>
                        </React.Fragment>
                      );
                    },
                  )}
                </div>
                {/* Hours 8-17 slots */}
                <div>
                  {generateFullDayTimeSlots().map((slot, index) => (
                    <div
                      key={index}
                      style={{
                        ...getSlotStyle(slot),
                        width: "30px", // half-hour slot
                        display: "inline-block",
                      }}
                      onClick={() => handleTimeSlotClick(slot)}
                    >
                      {/* No text inside slot */}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Legend */}
          </div>

          {tempTimeRange &&
            tempTimeRange.start &&
            showConfirmButton &&
            tempTimeRange.end && (
              <div
                style={{
                  border: "2px solid #ddd",
                  borderRadius: "8px",
                  padding: "15px",
                  backgroundColor: "#f8f9fa",
                  fontFamily: "Arial, sans-serif",
                  marginTop: "0",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <div>
                  {/* <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#333",
                    marginBottom: "8px",
                  }}
                >
                  {t("range_selezionato") || "Range selezionato"}
                </div> */}
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: "600",
                      color: "#555",
                      marginBottom: "12px",
                    }}
                  >
                    {tempTimeRange.start} - {tempTimeRange.end}
                  </div>
                  <Button primary onClick={onTimeRangeConfirm}>
                    {t("conferma")}
                  </Button>
                </div>
              </div>
            )}
        </div>
      </div>
    </>
  );
};

export default Timeline;
